import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import { computed, onMounted, readonly, ref } from 'vue'
import { getModelSuggestions } from '../types'
import type { AiModel, AiQueueItem, ConfidenceThresholds, ModelSuggestion } from '../types'
import { useAiDocumentApi } from './useAiDocumentApi'

/**
 * Model selection composable for AI document processing
 * Handles model loading, suggestions, confidence scoring, and confirmation workflow
 */
export function useModelSelection() {
  // API composable
  const api = useAiDocumentApi()

  // I18n composable
  const { t } = useCompositeI18n('core.AiDocumentProcessor')

  // Reactive state
  const availableModels = ref<AiModel[]>([])
  const selectedQueueItem = ref<AiQueueItem | null>(null)
  const selectedModelKey = ref<string>('')
  const modelSuggestions = ref<ModelSuggestion[]>([])
  const isConfirming = ref(false)

  // Confidence thresholds
  const confidenceThresholds: ConfidenceThresholds = {
    high: 0.8,
    medium: 0.6,
    low: 0.4,
  }

  // Computed properties
  const hasModels = computed(() => availableModels.value.length > 0)

  const currentSuggestions = computed(() => {
    if (!selectedQueueItem.value) return []
    return getModelSuggestions(selectedQueueItem.value) ?? []
  })

  const hasSuggestions = computed(() => currentSuggestions.value.length > 0)

  const bestSuggestion = computed(() => {
    if (!hasSuggestions.value) return null
    return currentSuggestions.value.reduce((best, current) => (current.confidence > best.confidence ? current : best))
  })

  const highConfidenceSuggestions = computed(() => {
    if (!hasSuggestions.value) return []
    return currentSuggestions.value.filter((suggestion) => suggestion.confidence >= confidenceThresholds.high)
  })

  const suggestionsWithModels = computed(() => {
    if (!hasSuggestions.value) return []

    return currentSuggestions.value
      .map((suggestion) => {
        const model = availableModels.value.find((m) => m.key === suggestion.model)
        return {
          ...suggestion,
          modelInfo: model,
        }
      })
      .filter((item) => item.modelInfo)
      .sort((a, b) => b.confidence - a.confidence)
  })

  const selectedModel = computed(() => {
    if (!selectedModelKey.value) return null
    return availableModels.value.find((model) => model.key === selectedModelKey.value) || null
  })

  const canConfirm = computed(
    () => selectedQueueItem.value && selectedModelKey.value && availableModels.value.some((model) => model.key === selectedModelKey.value),
  )

  const isAutoConfirmable = computed(() => {
    if (!bestSuggestion.value) return false
    return bestSuggestion.value.confidence >= confidenceThresholds.high
  })

  /**
   * Load available models
   */
  const loadModels = async (): Promise<void> => {
    const models = await api.getAvailableModels()
    if (models) {
      availableModels.value = models
    }
  }

  /**
   * Set queue item for model selection
   */
  const setQueueItem = (queueItem: AiQueueItem): void => {
    selectedQueueItem.value = queueItem
    modelSuggestions.value = getModelSuggestions(queueItem) ?? []

    // Auto-select best suggestion if available
    if (bestSuggestion.value) {
      selectedModelKey.value = bestSuggestion.value.model
    } else {
      selectedModelKey.value = ''
    }
  }

  /**
   * Clear selected queue item
   */
  const clearSelection = (): void => {
    selectedQueueItem.value = null
    selectedModelKey.value = ''
    modelSuggestions.value = []
  }

  /**
   * Select model by key
   */
  const selectModel = (modelKey: string): void => {
    if (availableModels.value.some((model) => model.key === modelKey)) {
      selectedModelKey.value = modelKey
    }
  }

  /**
   * Get confidence level for a score
   */
  const getConfidenceLevel = (confidence: number): 'high' | 'medium' | 'low' => {
    if (confidence >= confidenceThresholds.high) return 'high'
    if (confidence >= confidenceThresholds.medium) return 'medium'
    return 'low'
  }

  /**
   * Get confidence color for UI
   */
  const getConfidenceColor = (confidence: number): string => {
    const level = getConfidenceLevel(confidence)
    switch (level) {
      case 'high':
        return 'positive'
      case 'medium':
        return 'warning'
      case 'low':
        return 'negative'
    }
  }

  /**
   * Get confidence label
   */
  const getConfidenceLabel = (confidence: number): string => {
    const level = getConfidenceLevel(confidence)
    const percentage = Math.round(confidence * 100)

    return t(`modelSelection.confidenceLabel.${level}`, { percentage })
  }

  /**
   * Get model name by key
   */
  const getModelName = (modelKey: string): string => {
    const model = availableModels.value.find((m) => m.key === modelKey)
    return model?.name || modelKey
  }

  /**
   * Check if model exists
   */
  const isValidModel = (modelKey: string): boolean => {
    return availableModels.value.some((model) => model.key === modelKey)
  }

  /**
   * Confirm model selection for queue item
   */
  const confirmModel = async (): Promise<boolean> => {
    if (!canConfirm.value || !selectedQueueItem.value) {
      return false
    }

    try {
      isConfirming.value = true

      const success = await api.confirmModelType(selectedQueueItem.value.id, selectedModelKey.value)

      if (success) {
        // Update local queue item
        selectedQueueItem.value.confirmed_model_type = selectedModelKey.value
        selectedQueueItem.value.status = 'processing' as any // Type assertion for status update
      }

      return success
    } finally {
      isConfirming.value = false
    }
  }

  /**
   * Auto-confirm if confidence is high enough
   */
  const autoConfirm = async (): Promise<boolean> => {
    if (!isAutoConfirmable.value || !bestSuggestion.value) {
      return false
    }

    selectedModelKey.value = bestSuggestion.value.model
    return await confirmModel()
  }

  /**
   * Get suggestion rank (1-based)
   */
  const getSuggestionRank = (modelKey: string): number => {
    const sortedSuggestions = [...currentSuggestions.value].sort((a, b) => b.confidence - a.confidence)

    const index = sortedSuggestions.findIndex((s) => s.model === modelKey)
    return index !== -1 ? index + 1 : -1
  }

  /**
   * Filter models by search query
   */
  const filterModels = (query: string): AiModel[] => {
    if (!query.trim()) return availableModels.value

    const searchTerm = query.toLowerCase()
    return availableModels.value.filter(
      (model) =>
        model.name.toLowerCase().includes(searchTerm) || model.description.toLowerCase().includes(searchTerm) || model.key.toLowerCase().includes(searchTerm),
    )
  }

  /**
   * Get models sorted by suggestion confidence
   */
  const getModelsSortedBySuggestion = (): AiModel[] => {
    if (!hasSuggestions.value) {
      return [...availableModels.value].sort((a, b) => a.name.localeCompare(b.name))
    }

    const suggestionMap = new Map(currentSuggestions.value.map((s) => [s.model, s.confidence]))

    return [...availableModels.value].sort((a, b) => {
      const confidenceA = suggestionMap.get(a.key) || 0
      const confidenceB = suggestionMap.get(b.key) || 0

      if (confidenceA !== confidenceB) {
        return confidenceB - confidenceA // Higher confidence first
      }

      return a.name.localeCompare(b.name) // Alphabetical for same confidence
    })
  }

  /**
   * Reset selection to best suggestion
   */
  const resetToBestSuggestion = (): void => {
    if (bestSuggestion.value) {
      selectedModelKey.value = bestSuggestion.value.model
    }
  }

  /**
   * Check if a model is suggested
   */
  const isModelSuggested = (modelKey: string): boolean => {
    return currentSuggestions.value.some((suggestion) => suggestion.model === modelKey)
  }

  /**
   * Get suggestion for model
   */
  const getModelSuggestion = (modelKey: string): ModelSuggestion | null => {
    return currentSuggestions.value.find((suggestion) => suggestion.model === modelKey) || null
  }

  // Load models on mount
  onMounted(async () => {
    await loadModels()
  })

  // Return readonly state and actions
  return {
    // State (readonly)
    availableModels: readonly(availableModels),
    selectedQueueItem: readonly(selectedQueueItem),
    selectedModelKey: readonly(selectedModelKey),
    modelSuggestions: readonly(modelSuggestions),
    isConfirming: readonly(isConfirming),
    hasModels,
    hasSuggestions,
    bestSuggestion,
    highConfidenceSuggestions,
    suggestionsWithModels,
    selectedModel,
    canConfirm,
    isAutoConfirmable,
    confidenceThresholds,
    apiLoading: api.loading,
    apiError: api.error,

    // Actions
    loadModels,
    setQueueItem,
    clearSelection,
    selectModel,
    confirmModel,
    autoConfirm,
    resetToBestSuggestion,

    // Utility functions
    getConfidenceLevel,
    getConfidenceColor,
    getConfidenceLabel,
    getModelName,
    isValidModel,
    getSuggestionRank,
    filterModels,
    getModelsSortedBySuggestion,
    isModelSuggested,
    getModelSuggestion,

    // API actions
    clearApiError: api.clearError,
  } as const
}
